<template>
  <v-container class="pt-5 pb-7">
    <v-layout wrap>
      <v-flex v-if="loadingMetaData" class="text-xs-center pt-5">
        <v-progress-circular :size="70" :width="7" color="secondary" indeterminate />
      </v-flex>
      <v-flex v-else xs12>
        <v-form @submit.prevent="saveMetadata">
          <v-layout row wrap>
            <v-flex md3 mb-2 pr-3>
              <v-select v-model="updateForm.workflowStatusId" :items="workflowStatuses" box label="Status" item-text="name" item-value="id" />
            </v-flex>
            <v-flex md4 mb-2 px-3>
              <v-autocomplete
                v-model="getActivePeerReviewer"
                :items="peerReviewers"
                label="Peer Reviewer"
                box
                clearable
                item-value="id"
                item-text="fullName"
                placeholder="Start typing to Search"
              >
                <template v-slot:selection="data">
                  {{ data.item.fullName }} ({{ data.item.email }})
                </template>
                <template v-slot:item="data">
                  <v-list-tile-avatar>
                    <img :src="data.item.profileImageUrl">
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title v-html="data.item.fullName" />
                    <v-list-tile-sub-title v-html="data.item.email" />
                  </v-list-tile-content>
                </template>
              </v-autocomplete>
            </v-flex>
            <v-flex md3 mb-2 px-3>
              <v-menu v-model="reviewDateMenu" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field v-model="reviewDateFormatted" label="Next Scheduled Update" box @blur="reviewDate = parseDate(reviewDateFormatted)" v-on="on" />
                </template>
                <v-date-picker v-model="reviewDate" header-color="secondary" color="secondary" @input="reviewDateMenu = false" />
              </v-menu>
            </v-flex>
            <v-flex md2 mb-2 pl-3>
              <v-select v-model="updateForm.reviewPeriod" :items="['Annual', 'Biennial']" box label="Update Period" />
            </v-flex>
            <v-flex md6>
              <v-btn color="secondary" class="mx-0" type="submit" :loading="savingMetadata">
                Update
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-flex>
    </v-layout>

    <review-screen v-if="getWorkflowStatus !== 'Under Construction' && !loadingMetaData" :course-id="courseId" edit-access is-admin class="pt-8" />

    <template v-if="!loadingMetaData && getWorkflowStatus === 'Ready for Review'">
      <v-divider class="my-5" />
      <div class="text-xs-center">
        <h3>Finished with your peer review?</h3>
        <div class="mt-3">
          <v-btn color="secondary" @click="completeReview('Changes Requested')">
            Request Author Changes
          </v-btn>
          <v-btn color="secondary" @click="completeReview('No Changes Requested')">
            Ready for Publication
          </v-btn>
        </div>
      </div>
    </template>
  </v-container>
</template>
<script>
  import axios from 'axios';
  import moment from 'moment';
  import { mapActions } from 'vuex';
  import ManageCourseBase from '../ManageCourseBase.vue';
  import ReviewScreen from './ReviewScreen.vue';

  export default {

    components: {
      ReviewScreen,
    },
    extends: ManageCourseBase,
    props: {
      selectedTab: {
        type: String,
      },
    },

    data: vm => ({
      savingMetadata: false,
      loadingMetaData: true,
      updateForm: {
        workflowStatusId: null,
        reviewPeriod: '',
        nextReviewDate: new Date().toISOString().substr(0, 10),
      },
      newReviewer: null,
      reviewDateFormatted: vm.formatDate(new Date().toISOString().substr(0, 10)),
      reviewDateMenu: false,
      peerReviewers: [],
      workflowStatuses: [],
    }),
    computed: {
      reviewDate: {
        get() {
          if (moment(this.updateForm.nextReviewDate).format('MM/DD/YYYY') === '01/01/0001' || !this.updateForm.nextReviewDate) {
            return new Date().toISOString().substr(0, 10);
          }
          const [month, day, year] = moment(this.updateForm.nextReviewDate).format('MM/DD/YYYY').split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        },
        set(value) {
          this.updateForm.nextReviewDate = value;
        },
      },

      getActivePeerReviewer: {
        get() {
          if (this.updateForm.courseReviewers.length) {
            return this.updateForm.courseReviewers.find(x => x.active).userId;
          }

          return null;
        },

        set(val) {
          this.newReviewer = val;
        },
      },

      getWorkflowStatus() {
        if (this.updateForm) {
          return this.updateForm.workflowStatus ? this.updateForm.workflowStatus.name : null;
        }

        return null;
      },
    },

    watch: {
      'updateForm.nextReviewDate': function (val) {
        this.reviewDateFormatted = this.formatDate(val);
      },
      selectedTab(val) {
        if (val == 'Workflow') {
          this.getMetadata().then(() => {
            this.loadingMetaData = false;
          });
        }
      },
    },

    mounted() {
      this.getMetadata().then(() => {
        this.loadingMetaData = false;
      });
      this.getWorkflowStatuses();
      this.getPeerReviewers();
    },

    methods: {
      ...mapActions({
        setToast: 'toasts/addToGlobalToastQueue',
      }),

      async getMetadata() {
        const { data } = await axios.get(`/api/courses/${this.courseId}`);
        this.updateForm = data;
      },
      async getPeerReviewers() {
        const { data } = await axios.get('/api/users/dropdown?currentPage=1&pageSize=9999&role=peer-reviewer');
        this.peerReviewers = data.resultItems;
      },

      async saveMetadata() {
        this.savingMetadata = true;
        try {
          await axios.patch(`/api/courses/${this.courseId}`, this.updateForm);
          await this.saveReviewers();
          this.savingMetadata = false;
          this.setToast({
            color: 'success',
            message: 'Course has been saved',
            multiline: false,
          });
        } catch (error) {
          this.setToast({
            color: 'error',
            message: error.response.data[0],
            multiline: true,
          });
          this.savingMetadata = false;
        }
      },

      async saveReviewers() {
        if (this.newReviewer) {
          axios.post(`/api/courses/${this.courseId}/workflow/add-reviewer/${this.newReviewer}`);
        }
      },

      async completeReview(reviewType) {
        await axios.post(`/api/courses/${this.courseId}/workflow/complete-review?requestType=${reviewType}`);
        await this.getMetadata();
        this.setToast({
          color: 'success',
          message: 'This course has progressed to the next step',
          multiline: false,
        });
      },

      async getWorkflowStatuses() {
        const { data } = await axios.get('/api/workflowstatuses');
        this.workflowStatuses = data;
      },
    },
  };
</script>